import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
    BookOpen,
    FileText,
    Users,
    Plus,
    Upload,
    Download,
    Calendar,
    ArrowLeft,
    File,
    Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import { AIS_MODULE_PRESETS, formatAisModuleLabel, hasAisModuleMetadata } from '../utils/aisModules';
import { formatDueDate, isPastDue, normalizeDueDateForApi } from '../utils/assignmentDates';
import { parseRubricLines, parseTextList, rubricPointsTotal } from '../utils/rubricForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CourseDetail() {
    const { courseId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useAuth();
    const [course, setCourse] = useState(null);
    const [lessons, setLessons] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Dialogs
    const [showLessonDialog, setShowLessonDialog] = useState(false);
    const [showAssignmentDialog, setShowAssignmentDialog] = useState(false);
    const [selectedLesson, setSelectedLesson] = useState(null);
    
    // Forms
    const [lessonForm, setLessonForm] = useState({ title: '', week: 1, content: '' });
    const defaultAssignmentForm = { 
        title: '', 
        description: '', 
        due_date: '', 
        max_score: 100,
        module_number: '',
        module_title: '',
        ais_cycle: '',
        required_workspace_tabs: [],
        expected_deliverables: [],
        learning_outcomes_text: '',
        rubric_criteria_text: '',
    };
    const [assignmentForm, setAssignmentForm] = useState(defaultAssignmentForm);

    const updateAssignmentField = (field, value) => {
        setAssignmentForm(prev => ({ ...prev, [field]: value }));
    };

    const updateAssignmentDueDate = (event) => {
        updateAssignmentField('due_date', event.currentTarget.value);
    };
    
    const isInstructor = user.role === 'instructor';
    const getInitialTab = useCallback(() => {
        const requested = searchParams.get('tab');
        if (requested === 'assignments') return 'assignments';
        if (requested === 'students' && isInstructor) return 'students';
        return 'lessons';
    }, [searchParams, isInstructor]);
    const [activeTab, setActiveTab] = useState(() => getInitialTab());

    useEffect(() => {
        setActiveTab(getInitialTab());
    }, [getInitialTab]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (tab === 'lessons') {
            setSearchParams({});
        } else {
            setSearchParams({ tab });
        }
    };

    const fetchCourseData = useCallback(async () => {
        try {
            const [courseRes, lessonsRes, assignmentsRes] = await Promise.all([
                axios.get(`${API}/courses/${courseId}`),
                axios.get(`${API}/courses/${courseId}/lessons`),
                axios.get(`${API}/courses/${courseId}/assignments`)
            ]);
            
            setCourse(courseRes.data);
            setLessons(lessonsRes.data);
            setAssignments(assignmentsRes.data);
            
            if (isInstructor) {
                const studentsRes = await axios.get(`${API}/courses/${courseId}/students`);
                setStudents(studentsRes.data);
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Failed to load course data');
        } finally {
            setLoading(false);
        }
    }, [courseId, isInstructor]);

    useEffect(() => {
        fetchCourseData();
    }, [fetchCourseData]);

    const handleCreateLesson = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post(`${API}/lessons`, {
                ...lessonForm,
                course_id: courseId
            });
            setLessons([...lessons, response.data].sort((a, b) => a.week - b.week));
            setShowLessonDialog(false);
            setLessonForm({ title: '', week: 1, content: '' });
            toast.success('Lesson created!');
        } catch (error) {
            toast.error('Failed to create lesson');
        }
    };

    const handleCreateAssignment = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        const formDueDate = String(formData.get('due_date') || assignmentForm.due_date || '').trim();
        const formTitle = String(formData.get('title') || assignmentForm.title || '').trim();
        const formDescription = String(formData.get('description') || assignmentForm.description || '').trim();
        const parsedMaxScore = Number.parseInt(String(formData.get('max_score') || assignmentForm.max_score || 100), 10);
        const normalizedDueDate = normalizeDueDateForApi(formDueDate);
        if (!normalizedDueDate) {
            toast.error('Due date is required');
            return;
        }
        let rubricCriteria = [];
        try {
            rubricCriteria = parseRubricLines(assignmentForm.rubric_criteria_text);
        } catch (error) {
            toast.error(error.message);
            return;
        }
        const finalMaxScore = Number.isFinite(parsedMaxScore) ? parsedMaxScore : 100;
        if (rubricPointsTotal(rubricCriteria) > finalMaxScore) {
            toast.error('Rubric points cannot exceed max score');
            return;
        }

        try {
            const payload = {
                ...assignmentForm,
                title: formTitle,
                description: formDescription,
                course_id: courseId,
                due_date: normalizedDueDate,
                max_score: finalMaxScore,
                module_number: assignmentForm.module_number ? parseInt(assignmentForm.module_number) : null,
                learning_outcomes: parseTextList(assignmentForm.learning_outcomes_text),
                rubric_criteria: rubricCriteria,
            };
            delete payload.learning_outcomes_text;
            delete payload.rubric_criteria_text;
            const response = await axios.post(`${API}/assignments`, payload);
            setAssignments([...assignments, response.data]);
            setShowAssignmentDialog(false);
            setAssignmentForm(defaultAssignmentForm);
            toast.success('Assignment created!');
        } catch (error) {
            toast.error('Failed to create assignment');
        }
    };

    const applyAssignmentModulePreset = (presetId) => {
        if (presetId === 'none') {
            setAssignmentForm({
                ...assignmentForm,
                module_number: '',
                module_title: '',
                ais_cycle: '',
                required_workspace_tabs: [],
                expected_deliverables: [],
            });
            return;
        }

        const preset = AIS_MODULE_PRESETS.find((item) => item.id === presetId);
        if (!preset) return;

        setAssignmentForm({
            ...assignmentForm,
            module_number: String(preset.module_number),
            module_title: preset.module_title,
            ais_cycle: preset.ais_cycle,
            required_workspace_tabs: preset.required_workspace_tabs,
            expected_deliverables: preset.expected_deliverables,
        });
    };

    const handleUploadFile = async (lessonId, file) => {
        const formData = new FormData();
        formData.append('file', file);
        
        try {
            const response = await axios.post(`${API}/lessons/${lessonId}/upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            setLessons(lessons.map(l => 
                l.id === lessonId 
                    ? { ...l, file_url: response.data.file_url, file_name: response.data.file_name }
                    : l
            ));
            toast.success('File uploaded!');
        } catch (error) {
            toast.error('Failed to upload file');
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="h-8 w-64 skeleton" />
                <div className="h-48 skeleton rounded-2xl" />
            </div>
        );
    }

    if (!course) {
        return (
            <div className="text-center py-12">
                <p className="text-slate-500">Course not found</p>
                <Link to="/courses">
                    <Button variant="outline" className="mt-4">Back to Courses</Button>
                </Link>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="course-detail-page">
            {/* Back button & Header */}
            <div>
                <Link to="/courses" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-4">
                    <ArrowLeft className="w-4 h-4" />
                    Back to Courses
                </Link>
                
                <div className="bg-gradient-to-r from-[#0F2A44] to-[#1E3A8A] rounded-2xl p-6 md:p-8 text-white">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <span className="text-white/70 text-sm font-medium">{course.code}</span>
                            <h1 className="text-2xl md:text-3xl font-bold font-['Manrope'] mt-1">{course.title}</h1>
                            <p className="text-white/80 mt-2 max-w-2xl">{course.description}</p>
                            <div className="flex items-center gap-4 mt-4 text-sm text-white/70">
                                <span className="flex items-center gap-1">
                                    <Users className="w-4 h-4" />
                                    {course.enrolled_count} students
                                </span>
                                <span className="flex items-center gap-1">
                                    <BookOpen className="w-4 h-4" />
                                    {lessons.length} lessons
                                </span>
                                <span className="flex items-center gap-1">
                                    <FileText className="w-4 h-4" />
                                    {assignments.length} assignments
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
                <TabsList className="bg-slate-100 p-1 rounded-xl">
                    <TabsTrigger value="lessons" className="rounded-lg data-[state=active]:bg-white">
                        Lessons
                    </TabsTrigger>
                    <TabsTrigger value="assignments" className="rounded-lg data-[state=active]:bg-white">
                        Assignments
                    </TabsTrigger>
                    {isInstructor && (
                        <TabsTrigger value="students" className="rounded-lg data-[state=active]:bg-white">
                            Students
                        </TabsTrigger>
                    )}
                </TabsList>

                {/* Lessons Tab */}
                <TabsContent value="lessons" className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold font-['Manrope']">Course Lessons</h2>
                        {isInstructor && (
                            <Button
                                onClick={() => setShowLessonDialog(true)}
                                className="bg-[#2F8FCB] hover:bg-[#2577a8] rounded-full"
                                data-testid="add-lesson-btn"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Add Lesson
                            </Button>
                        )}
                    </div>

                    {lessons.length === 0 ? (
                        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                            <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                            <p className="text-slate-500">No lessons yet</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {lessons.map((lesson) => (
                                <div
                                    key={lesson.id}
                                    className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-md transition-shadow"
                                    data-testid={`lesson-${lesson.id}`}
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex items-start gap-4">
                                            <div className="w-10 h-10 rounded-lg bg-[#E0F2FE] flex items-center justify-center flex-shrink-0">
                                                <span className="text-[#2F8FCB] font-semibold text-sm">W{lesson.week}</span>
                                            </div>
                                            <div>
                                                <h3 className="font-medium text-slate-900">{lesson.title}</h3>
                                                {lesson.content && (
                                                    <p className="text-sm text-slate-500 mt-1 line-clamp-2">{lesson.content}</p>
                                                )}
                                                {lesson.file_url && (
                                                    <a
                                                        href={`${process.env.REACT_APP_BACKEND_URL}${lesson.file_url}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-2 mt-2 text-sm text-[#2F8FCB] hover:text-[#0F2A44]"
                                                    >
                                                        <File className="w-4 h-4" />
                                                        {lesson.file_name}
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setSelectedLesson(lesson)}
                                                data-testid={`view-lesson-${lesson.id}`}
                                            >
                                                View
                                            </Button>
                                            {isInstructor && (
                                                <label className="cursor-pointer">
                                                    <input
                                                        type="file"
                                                        className="hidden"
                                                        onChange={(e) => {
                                                            if (e.target.files[0]) {
                                                                handleUploadFile(lesson.id, e.target.files[0]);
                                                            }
                                                        }}
                                                    />
                                                    <div className="inline-flex items-center gap-2 px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">
                                                        <Upload className="w-4 h-4 text-slate-400" />
                                                        Upload file
                                                    </div>
                                                </label>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </TabsContent>

                {/* Assignments Tab */}
                <TabsContent value="assignments" className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold font-['Manrope']">Assignments</h2>
                        {isInstructor && (
                            <Button
                                onClick={() => setShowAssignmentDialog(true)}
                                className="bg-[#2F8FCB] hover:bg-[#2577a8] rounded-full"
                                data-testid="add-assignment-btn"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Add Assignment
                            </Button>
                        )}
                    </div>

                    {assignments.length === 0 ? (
                        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                            <p className="text-slate-500">No assignments yet</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {assignments.map((assignment) => {
                                const isOverdue = isPastDue(assignment.due_date);
                                
                                return (
                                    <Link
                                        key={assignment.id}
                                        to={`/assignments/${assignment.id}`}
                                        state={{ fromCourseId: courseId }}
                                        className="bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow"
                                        data-testid={`assignment-${assignment.id}`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                                isOverdue ? 'bg-red-50' : 'bg-emerald-50'
                                            }`}>
                                                <FileText className={`w-5 h-5 ${
                                                    isOverdue ? 'text-red-500' : 'text-emerald-500'
                                                }`} />
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="font-medium text-slate-900">{assignment.title}</h3>
                                                {hasAisModuleMetadata(assignment) && (
                                                    <div className="mt-1 inline-flex items-center rounded-full bg-sky-50 border border-sky-100 px-2 py-0.5 text-[11px] font-medium text-sky-700">
                                                        {formatAisModuleLabel(assignment)}
                                                    </div>
                                                )}
                                                <p className="text-sm text-slate-500 mt-1 line-clamp-2">{assignment.description}</p>
                                                <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                                        <span className="flex items-center gap-1">
                                            <Calendar className="w-3 h-3" />
                                            Due: {formatDueDate(assignment.due_date)}
                                        </span>
                                                    <span>Max: {assignment.max_score} pts</span>
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </TabsContent>

                {/* Students Tab (Instructor only) */}
                {isInstructor && (
                    <TabsContent value="students" className="space-y-4">
                        <h2 className="text-lg font-semibold font-['Manrope']">Enrolled Students</h2>
                        
                        {students.length === 0 ? (
                            <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                                <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                                <p className="text-slate-500">No students enrolled yet</p>
                            </div>
                        ) : (
                            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Student Name</th>
                                            <th>Enrolled Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {students.map((student) => (
                                            <tr key={student.id}>
                                                <td className="font-medium">{student.student_name}</td>
                                                <td>{new Date(student.enrolled_at).toLocaleDateString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </TabsContent>
                )}
            </Tabs>

            {/* Lesson Detail Dialog */}
            <Dialog open={!!selectedLesson} onOpenChange={(open) => !open && setSelectedLesson(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle className="font-['Manrope']">{selectedLesson?.title}</DialogTitle>
                        <DialogDescription>
                            Review the lesson content and any attached course material.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedLesson && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                                <Calendar className="w-4 h-4" />
                                <span>Week {selectedLesson.week}</span>
                            </div>
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 whitespace-pre-wrap max-h-[50vh] overflow-y-auto">
                                {selectedLesson.content || 'No lesson content has been added yet.'}
                            </div>
                            {selectedLesson.file_url && (
                                <a
                                    href={`${process.env.REACT_APP_BACKEND_URL}${selectedLesson.file_url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-sm text-[#2F8FCB] hover:text-[#0F2A44]"
                                >
                                    <File className="w-4 h-4" />
                                    {selectedLesson.file_name || 'Open attached file'}
                                </a>
                            )}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSelectedLesson(null)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Create Lesson Dialog */}
            <Dialog open={showLessonDialog} onOpenChange={setShowLessonDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-['Manrope']">Add New Lesson</DialogTitle>
                        <DialogDescription>
                            Create a lesson for this course. Files can be uploaded after the lesson is created.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateLesson} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label>Week</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={lessonForm.week}
                                    onChange={(e) => setLessonForm({ ...lessonForm, week: parseInt(e.target.value) })}
                                    className="mt-1"
                                    required
                                />
                            </div>
                            <div>
                                <Label>Title</Label>
                                <Input
                                    value={lessonForm.title}
                                    onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })}
                                    placeholder="Lesson title"
                                    className="mt-1"
                                    required
                                />
                            </div>
                        </div>
                        <div>
                            <Label>Content (optional)</Label>
                            <Textarea
                                value={lessonForm.content}
                                onChange={(e) => setLessonForm({ ...lessonForm, content: e.target.value })}
                                placeholder="Lesson description or notes..."
                                className="mt-1"
                                rows={3}
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setShowLessonDialog(false)} className="rounded-full">
                                Cancel
                            </Button>
                            <Button type="submit" className="bg-[#0F2A44] hover:bg-[#1E3A8A] rounded-full">
                                Create Lesson
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Create Assignment Dialog */}
            <Dialog open={showAssignmentDialog} onOpenChange={setShowAssignmentDialog}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-['Manrope']">Create Assignment</DialogTitle>
                        <DialogDescription>
                            Add assignment instructions, due date, and maximum score for students.
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreateAssignment} className="space-y-4">
                        <div>
                            <Label htmlFor="course-assignment-title">Title</Label>
                            <Input
                                id="course-assignment-title"
                                name="title"
                                value={assignmentForm.title}
                                onChange={(e) => updateAssignmentField('title', e.target.value)}
                                placeholder="Assignment title"
                                className="mt-1"
                                required
                                data-testid="course-assignment-title-input"
                            />
                        </div>
                        <div>
                            <Label htmlFor="course-assignment-description">Description</Label>
                            <Textarea
                                id="course-assignment-description"
                                name="description"
                                value={assignmentForm.description}
                                onChange={(e) => updateAssignmentField('description', e.target.value)}
                                placeholder="Instructions for students..."
                                className="mt-1"
                                rows={3}
                                required
                                data-testid="course-assignment-description-input"
                            />
                        </div>
                        <div className="rounded-xl border border-sky-100 bg-sky-50/60 p-4 space-y-3">
                            <div>
                                <Label>AIS Module Pattern</Label>
                                <Select
                                    value={assignmentForm.module_number ? `module-${assignmentForm.module_number}` : 'none'}
                                    onValueChange={applyAssignmentModulePreset}
                                >
                                    <SelectTrigger className="mt-1 bg-white" data-testid="course-assignment-ais-module-select">
                                        <SelectValue placeholder="Optional AIS module alignment" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">No AIS module alignment</SelectItem>
                                        {AIS_MODULE_PRESETS.map((preset) => (
                                            <SelectItem key={preset.id} value={preset.id}>
                                                Module {preset.module_number} - {preset.module_title}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            {assignmentForm.module_number && (
                                <div className="grid gap-2 text-xs text-slate-600">
                                    <div><span className="font-semibold text-slate-700">Cycle:</span> {assignmentForm.ais_cycle}</div>
                                    <div><span className="font-semibold text-slate-700">Workspace:</span> {assignmentForm.required_workspace_tabs.join(', ')}</div>
                                    <div><span className="font-semibold text-slate-700">Deliverables:</span> {assignmentForm.expected_deliverables.join(', ')}</div>
                                </div>
                            )}
                        </div>
                        <div className="rounded-xl border border-violet-100 bg-violet-50/50 p-4 space-y-3" data-testid="course-assignment-rubric-outcomes-panel">
                            <div>
                                <Label htmlFor="course-assignment-learning-outcomes">Learning Outcomes</Label>
                                <Textarea
                                    id="course-assignment-learning-outcomes"
                                    value={assignmentForm.learning_outcomes_text}
                                    onChange={(e) => updateAssignmentField('learning_outcomes_text', e.target.value)}
                                    placeholder={"One outcome per line, for example:\nPrepare balanced journal entries\nExplain posting impact on the general ledger"}
                                    className="mt-1 bg-white"
                                    rows={3}
                                    data-testid="course-assignment-learning-outcomes-input"
                                />
                            </div>
                            <div>
                                <Label htmlFor="course-assignment-rubric-criteria">Rubric Criteria</Label>
                                <Textarea
                                    id="course-assignment-rubric-criteria"
                                    value={assignmentForm.rubric_criteria_text}
                                    onChange={(e) => updateAssignmentField('rubric_criteria_text', e.target.value)}
                                    placeholder={"Use: Criterion | points | optional description\nJournal entry accuracy | 40 | Debits and credits are correct\nEvidence and explanation | 20"}
                                    className="mt-1 bg-white"
                                    rows={4}
                                    data-testid="course-assignment-rubric-criteria-input"
                                />
                                <p className="text-xs text-slate-500 mt-1">Keep this simple for AIS students. Total rubric points must not exceed max score.</p>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="course-assignment-due-date">Due Date</Label>
                                <Input
                                    id="course-assignment-due-date"
                                    name="due_date"
                                    type="date"
                                    value={assignmentForm.due_date}
                                    onInput={updateAssignmentDueDate}
                                    onChange={updateAssignmentDueDate}
                                    onBlur={updateAssignmentDueDate}
                                    className="mt-1"
                                    required
                                    data-testid="course-assignment-due-input"
                                />
                            </div>
                            <div>
                                <Label htmlFor="course-assignment-max-score">Max Score</Label>
                                <Input
                                    id="course-assignment-max-score"
                                    name="max_score"
                                    type="number"
                                    min="1"
                                    value={assignmentForm.max_score}
                                    onChange={(e) => updateAssignmentField('max_score', parseInt(e.target.value))}
                                    className="mt-1"
                                    required
                                    data-testid="course-assignment-score-input"
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setShowAssignmentDialog(false)} className="rounded-full">
                                Cancel
                            </Button>
                            <Button type="submit" className="bg-[#0F2A44] hover:bg-[#1E3A8A] rounded-full">
                                Create Assignment
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
